import { defineRegistry } from "@json-render/react";
import { Catalog } from "./Catalog";
import {
  NotificationText,
  NotificationTextProps,
} from "@/src/components/Chat/JsonRenderComponents/NotificationText";
import {
  NotificationCardContainer,
  NotificationCardContainerProps,
} from "@/src/components/Chat/JsonRenderComponents/NotificationCardContainer";
import {
  AssetRecommendationList,
  AssetRecommendationListProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetRecommendationList";
import {
  AssetHeader,
  AssetHeaderProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetHeader";
import {
  MultiAssetDimensionScoreComparisonTable,
  MultiAssetDimensionScoreComparisonTableProps,
} from "@/src/components/Chat/JsonRenderComponents/MultiAssetDimensionScoreComparisonTable";
import {
  AssetDimensionScoreTable,
  AssetDimensionScoreTableProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetDimensionScoreTable";
import {
  AssetDataGridTable,
  AssetDataGridTableProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetDataGridTable";
import {
  AssetKeyStatsStrip,
  AssetKeyStatsStripProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetKeyStatsStrip";
import {
  AssetDimensionRankChangeTable,
  AssetDimensionRankChangeTableProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetDimensionRankChangeTable";
import {
  AssetRatingChangeComparisonList,
  AssetRatingChangeComparisonListProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetRatingChangeComparisonList";

import {
  AssetNarrativeHighlightsList,
  AssetNarrativeHighlightsListProps,
} from "@/src/components/Chat/JsonRenderComponents/AssetNarrativeHighlightsList";
import {
  NotificationButtonList,
  NotificationButtonListProps,
} from "@/src/components/Chat/JsonRenderComponents/NotificationButtonList";
import {
  AssetVolumeComparisonCard,
  AssetVolumeComparisonCardProps,
  AssetPriceSparklineCard,
  AssetPriceSparklineCardProps,
  AssetRsiGaugeCard,
  AssetRsiGaugeCardProps,
  AssetKdjGaugeCard,
  AssetKdjGaugeCardProps,
  AssetMacdMiniChartCard,
  AssetMacdMiniChartCardProps,
  AssetEmaCandleChartCard,
  AssetEmaCandleChartCardProps,
  AssetSentimentRatioCard,
  AssetSentimentRatioCardProps,
  AssetEarningsActualExpectedCard,
  AssetEarningsActualExpectedCardProps,
  AssetInsightSummaryCard,
  AssetInsightSummaryCardProps,
  AssetTradePotentialCard,
  AssetTradePotentialCardProps,
  AssetSectorShiftCard,
  AssetSectorShiftCardProps,
} from "@/src/components/Chat/JsonRenderComponents/signalCards";
import { FitnessScoreCardRegistryEntry } from "./JsonRenderComponents/FitnessScoreCard/FitnessScoreCardRegistryEntry";
import type { FitnessScoreCardProps } from "./JsonRenderComponents/FitnessScoreCard/types";
import {
  AssetPriceBusinessChart,
  ThemeHeatmapBusinessChart,
  AssetPriceForecastBusinessChart,
  AssetAnalystRatingBusinessChart,
  AssetTechnicalSignalSummaryBusinessChart,
  AssetTechnicalSignalDetailBusinessChart,
  AssetHistoricalEarningsBusinessChart,
  AssetDividendInfoBusinessChart,
  AssetDividendHistoryBusinessChart,
  AssetFinancialsBusinessChart,
  WhaleHoldingChangeBusinessChart,
} from "@/src/components/Chat/BusinessComponents";
import { BusinessComponentWrapper } from "@/src/components/Chat/BusinessComponents/BusinessComponentWrapper";

const { registry } = defineRegistry(Catalog, {
  components: {
    Card: ({ props, children }) => (
      <div className="card">
        <h3>{props.title}</h3>
        {children}
      </div>
    ),
    Metric: ({ props }) => (
      <div className="flex flex-col gap-0.5">
        <span className="body_s text-content_tertiary">{props.label}</span>
        <span className="title_s text-content_primary">{props.value}</span>
      </div>
    ),
    Button: ({ props }) => (
      <button
        type="button"
        className="body_s text-content_brand_primary"
        data-openreplay-label={`StructuredCards JsonRender · Button | ${props.label}`}
      >
        {props.label}
      </button>
    ),
    NotificationText: ({ props }: { props: NotificationTextProps }) => (
      <NotificationText {...props} />
    ),
    NotificationCardContainer: ({
      props,
      children,
    }: {
      props: Omit<NotificationCardContainerProps, "children">;
      children?: React.ReactNode;
    }) => (
      <NotificationCardContainer {...props}>
        {children}
      </NotificationCardContainer>
    ),
    AssetRecommendationList: ({
      props,
    }: {
      props: AssetRecommendationListProps;
    }) => <AssetRecommendationList {...props} />,
    AssetHeader: ({ props }: { props: AssetHeaderProps }) => (
      <AssetHeader {...props} />
    ),
    MultiAssetDimensionScoreComparisonTable: ({
      props,
    }: {
      props: MultiAssetDimensionScoreComparisonTableProps;
    }) => <MultiAssetDimensionScoreComparisonTable {...props} />,
    AssetDimensionScoreTable: ({
      props,
    }: {
      props: AssetDimensionScoreTableProps;
    }) => <AssetDimensionScoreTable {...props} />,
    AssetDataGridTable: ({ props }: { props: AssetDataGridTableProps }) => (
      <AssetDataGridTable {...props} />
    ),
    AssetKeyStatsStrip: ({ props }: { props: AssetKeyStatsStripProps }) => (
      <AssetKeyStatsStrip {...props} />
    ),
    AssetDimensionRankChangeTable: ({
      props,
    }: {
      props: AssetDimensionRankChangeTableProps;
    }) => <AssetDimensionRankChangeTable {...props} />,
    AssetRatingChangeComparisonList: ({
      props,
    }: {
      props: AssetRatingChangeComparisonListProps;
    }) => <AssetRatingChangeComparisonList {...props} />,
    AssetNarrativeHighlightsList: ({
      props,
    }: {
      props: AssetNarrativeHighlightsListProps;
    }) => <AssetNarrativeHighlightsList {...props} />,
    NotificationButtonList: ({
      props,
    }: {
      props: NotificationButtonListProps;
    }) => <NotificationButtonList {...props} />,

    AssetVolumeComparisonCard: ({
      props,
    }: {
      props: AssetVolumeComparisonCardProps;
    }) => <AssetVolumeComparisonCard {...props} />,
    AssetPriceSparklineCard: ({
      props,
    }: {
      props: AssetPriceSparklineCardProps;
    }) => <AssetPriceSparklineCard {...props} />,
    AssetRsiGaugeCard: ({ props }: { props: AssetRsiGaugeCardProps }) => (
      <AssetRsiGaugeCard {...props} />
    ),
    AssetKdjGaugeCard: ({ props }: { props: AssetKdjGaugeCardProps }) => (
      <AssetKdjGaugeCard {...props} />
    ),
    AssetMacdMiniChartCard: ({
      props,
    }: {
      props: AssetMacdMiniChartCardProps;
    }) => <AssetMacdMiniChartCard {...props} />,
    AssetEmaCandleChartCard: ({
      props,
    }: {
      props: AssetEmaCandleChartCardProps;
    }) => <AssetEmaCandleChartCard {...props} />,
    AssetSentimentRatioCard: ({
      props,
    }: {
      props: AssetSentimentRatioCardProps;
    }) => <AssetSentimentRatioCard {...props} />,
    AssetEarningsActualExpectedCard: ({
      props,
    }: {
      props: AssetEarningsActualExpectedCardProps;
    }) => <AssetEarningsActualExpectedCard {...props} />,
    AssetInsightSummaryCard: ({
      props,
    }: {
      props: AssetInsightSummaryCardProps;
    }) => <AssetInsightSummaryCard {...props} />,
    AssetTradePotentialCard: ({
      props,
    }: {
      props: AssetTradePotentialCardProps;
    }) => <AssetTradePotentialCard {...props} />,
    AssetSectorShiftCard: ({ props }: { props: AssetSectorShiftCardProps }) => (
      <AssetSectorShiftCard {...props} />
    ),
    FitnessScoreCard: ({
      props,
    }: {
      props: Omit<FitnessScoreCardProps, "onSeeFullScore">;
    }) => <FitnessScoreCardRegistryEntry props={props} />,

    // ─── Business Components ───
    AssetPriceBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetPriceBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    ThemeHeatmapBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <ThemeHeatmapBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetPriceForecastBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetPriceForecastBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetAnalystRatingBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetAnalystRatingBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetTechnicalSignalSummaryBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetTechnicalSignalSummaryBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetTechnicalSignalDetailBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetTechnicalSignalDetailBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetHistoricalEarningsBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetHistoricalEarningsBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetDividendInfoBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetDividendInfoBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetDividendHistoryBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetDividendHistoryBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    AssetFinancialsBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <AssetFinancialsBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
    WhaleHoldingChangeBusinessChart: ({ props }: { props: any }) => (
      <BusinessComponentWrapper title={props.title}>
        <WhaleHoldingChangeBusinessChart {...props} />
      </BusinessComponentWrapper>
    ),
  },
  actions: {
    export_report: async () => {},
    refresh_data: async () => {},
  },
});

export default registry;
